import { useEffect, useCallback } from 'react'
import { adjustArrayToLength, needsArrayAdjustment, createArrayAdjustmentConfig } from '../utils/arrayUtils'

/**
 * Custom hook to handle dynamic array adjustments based on frequency changes.
 *
 * This hook ensures that form arrays (eventOccurrence, dayOfTheWeek, dayOfTheMonth)
 * always have the correct length to match the current frequency setting. When frequency
 * changes, arrays are either expanded with default values or truncated while preserving
 * existing valid data.
 *
 * @param {boolean} open - Whether the dialog is currently open (only runs when true)
 * @param {number} currentFrequency - The current frequency value (1-7 typically)
 * @param {Object} formData - The current form data containing the arrays to adjust
 * @param {Array|*} formData.eventOccurrence - Week of month occurrence values
 * @param {Array|*} formData.dayOfTheWeek - Day of week values
 * @param {Array|*} formData.dayOfTheMonth - Day of month values
 * @param {Function} setValue - React Hook Form setValue function to update form fields
 *
 * @example
 * // Usage in component:
 * useFrequencyArrayAdjustment(open, currentFrequency, formData, setValue)
 *
 * // When frequency changes from 2 to 4:
 * // Before: eventOccurrence = [1, 2]
 * // After:  eventOccurrence = [1, 2, 1, 1] (filled with defaults)
 *
 * // When frequency changes from 4 to 2:
 * // Before: eventOccurrence = [1, 2, 3, 4]
 * // After:  eventOccurrence = [1, 2] (truncated)
 */
export const useFrequencyArrayAdjustment = (open, currentFrequency, formData, setValue) => {
  // Memoize the adjustment configuration to prevent recreation
  const adjustmentConfig = createArrayAdjustmentConfig(currentFrequency)

  // Memoize the adjustment function to prevent unnecessary re-renders
  const adjustArrays = useCallback(() => {
    if (!open || currentFrequency <= 0) return

    const { eventOccurrence, dayOfTheWeek, dayOfTheMonth } = formData

    // Batch setValue calls to prevent multiple re-renders
    const updates = {}

    // Check and adjust eventOccurrence
    if (needsArrayAdjustment(eventOccurrence, currentFrequency)) {
      const config = adjustmentConfig.eventOccurrence
      updates.eventOccurrence = adjustArrayToLength(eventOccurrence, config.targetLength, config.defaultValue, config.validator)
    }

    // Check and adjust dayOfTheWeek
    if (needsArrayAdjustment(dayOfTheWeek, currentFrequency)) {
      const config = adjustmentConfig.dayOfTheWeek
      updates.dayOfTheWeek = adjustArrayToLength(dayOfTheWeek, config.targetLength, config.defaultValue, config.validator)
    }

    // Check and adjust dayOfTheMonth
    if (needsArrayAdjustment(dayOfTheMonth, currentFrequency)) {
      const config = adjustmentConfig.dayOfTheMonth
      updates.dayOfTheMonth = adjustArrayToLength(dayOfTheMonth, config.targetLength, config.defaultValue, config.validator)
    }

    // Apply all updates at once to minimize re-renders
    Object.entries(updates).forEach(([field, value]) => {
      setValue(field, value)
    })
  }, [open, currentFrequency, formData, setValue, adjustmentConfig])

  useEffect(() => {
    adjustArrays()
  }, [adjustArrays])
}
